import { ReactNode, useEffect, useRef, useState } from 'react';
import { animated, useTransition, useSpring } from 'react-spring';
import { GetServerSidePropsContext, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  COOKIES_CONFIG,
  INTERCOM_CONFIG,
  ImpressionTracker,
  PageViewTracker,
  template,
  US_STATES_CODES,
  BASE_PATHS,
  PATHS,
  NEW_GUARANTEED_TIME_TO_LOVE_HOMEOWNERS_LANDING_PAGE_EXPERIMENT,
  HOMEOWNERS_HERO_COPY_V2_EXPERIMENT,
} from '@belong/common';
import {
  Text,
  VideoSideContents,
  TestimonialsCarousel,
  GoogleStars,
  Link,
  Image,
  Button,
} from '@belong/ui';
import { PrismicDocument } from '@prismicio/types';
import clsx from 'clsx';
import { kebabCase } from 'es-toolkit/compat';
import { nanoid } from 'nanoid';
import { parseCookies } from 'nookies';
import { fetchCityListService } from 'src/api/geo';
import { fetchHomeownerCountService } from 'src/api/homeowner';
import type { InitialZustandState } from 'src/store/region';
import { fetchRegionsService } from 'src/api/region';
import { trackEventService } from 'src/api/tracking';
import { type IntercomWidgetProps } from 'src/components/intercom-widget/intercom-widget';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { RefreshMiamiBanner } from 'src/components/refresh-miami-banner/refresh-miami-banner';
import { SfistBayAreaBanner } from 'src/components/sfist-bay-area-banner/sfist-bay-area-banner';
import { StepsSection } from 'src/components/steps-section/steps-section';
import { StepsSectionData } from 'src/components/steps-section/steps-section';
import {
  fetchAllBlogProfiles,
  fetchBlogPostsList,
} from 'src/features/cms/prismic';
import { SanDiegoShortTermRentalsBanner } from 'src/features/homeowners/componentes/san-diego-short-term-rentals-banner/san-diego-short-term-rentals-banner';
import {
  GA_EVENTS,
  GA_ENTRY_POINTS,
  GA_EXPERIMENT,
} from 'src/features/rent-estimate/constants';
import { useUser } from 'src/features/users/useUser';
import { TownTourSection } from 'src/modals/how-it-works/town-tour/town-tour-section';
import { createClient } from 'src/prismicio';
import { useStore } from 'src/store';
import { regionSelector, somewhereElse } from 'src/store/region';
import { getVariant } from 'src/features/ab-testing/utils';
import {
  getExperimentVariantFromCookies,
  getExperimentsFromCookies,
} from 'src/utils/experiments';
import { getRegionFromCookies } from 'src/utils/get-region-from-cookies';
import { buildServerClient } from 'src/utils/server-client';
import {
  NAVBAR_ACTIONS,
  REGION_ID_TO_COVER_IMAGE_MAP,
  DEFAULT_COVER_IMAGE,
} from '../../constants';
import css from '../../homeowners.module.css';
import { HOMEOWNERS_STRINGS as STRINGS } from '../../homeowners.strings';
import { CoverageCity } from '../../homeowners.types';
import { useOnScreen } from '../../hooks/use-on-screen';
import { BlogCarousel } from '../blog-carousel/blog-carousel';
import { CalculatorSectionVariant } from '../calculator-section/calculator-section-variant';
import { Cover } from '../cover/cover';
import { CoverageMap } from '../coverage-map/coverage-map';
import { Footbar } from '../footbar/footbar';
import { HomeownersTrustBelongSectionProps } from '../homeowners-trust-belong-section/homeowners-trust-belong-section';
import { PriceTablePreview } from '../price-table-preview/price-table-preview';
import { ReferralMessage } from '../referral-message/referral-message';
import { StickyNavbar } from '../sticky-navbar/sticky-navbar';

const IntercomWidget = dynamic<IntercomWidgetProps>(
  () =>
    import('src/components/intercom-widget/intercom-widget').then(
      (mod) => mod.IntercomWidget,
    ),
  { ssr: false },
);

const HomeownersTrustBelongSection = dynamic<HomeownersTrustBelongSectionProps>(
  () =>
    import('../homeowners-trust-belong-section/homeowners-trust-belong-section').then(
      (mod) => mod.HomeownersTrustBelongSection,
    ),
  { ssr: false },
);

const LessVacancySection = dynamic<unknown>(
  () =>
    import('../less-vacancy-section/less-vacancy-section').then(
      (mod) => mod.LessVacancySection,
    ),
  { ssr: false },
);

const STEPS_SECTIONS_DATA = {
  section1: [
    {
      src: 'homeowner-lp/ho_lp_section_02.jpg',
      title: (
        <div className={css.stepsSection1Card1}>
          {'Guaranteed Rental\u00a0Payments'}
        </div>
      ),
      hoverState: {
        title: (
          <div className={css.stepsSection1Card1Hover}>
            Get paid on time, every time.
          </div>
        ),
        body: 'With Guaranteed Rent, you get paid on the first, even if your residents are late on\u00a0rent.',
      },
    },
    {
      src: 'homeowner-lp/ho_lp_section_04.jpg',
      title: (
        <div className={css.stepsSection1Card3}>
          {'Maximized Rental\u00a0Value'}
        </div>
      ),
      hoverState: {
        title: (
          <div className={css.stepsSection1Card3Hover}>
            Get top dollar for your home. Always.
          </div>
        ),
        body: 'Our technology-first approach uses true market data and real-time interest from potential residents to maximize listing\u00a0prices.',
      },
    },
    {
      src: 'homeowner-lp/ho_lp_section_03.jpg',
      title: <div className={css.stepsSection1Card2}>Financial Alignment</div>,
      hoverState: {
        title: (
          <div className={css.stepsSection1Card2Hover}>
            {'We partner with you to maximize long\u2011term\u00a0wealth.'}
          </div>
        ),
        body: "We don't make money if you aren't first, starting with a complimentary wealth analysis of your home to understand long-term gains and monthly cash\u00a0flow.",
      },
    },
  ],
  section2: [
    {
      src: 'homeowner-lp/ho_lp_section_05.png',
      title: (
        <div className={css.stepsSection2Card1}>24/7 Emergency Support</div>
      ),
      hoverState: {
        title: (
          <div className={css.stepsSection2Card1Hover}>
            {'No more phone tag. We’re here\u00a0for\u00a0you.'}
          </div>
        ),
        body: 'To care for you and your home, your dedicated Concierge works with a team to offer 24/7\u00a0support.',
      },
    },
    {
      src: 'homeowner-lp/ho_lp_section_06.v2.png',
      title: (
        <div className={css.stepsSection2Card2}>
          {'Guaranteed Repairs Done\u00a0By\u00a0Quality\u00a0Pros'}
        </div>
      ),
      hoverState: {
        title: (
          <div className={css.stepsSection2Card2Hover}>
            {'Instant access to 10,000+\u00a0Pros.'}
          </div>
        ),
        body: 'With our expansive Pro network, we ensure all maintenance repairs are high quality and at market\u00a0rates.',
      },
    },
    {
      src: 'belong-x/ho_lp_section_07_v2.png',
      title: (
        <div className={css.stepsSection2Card3}>
          {'Resident-Led Resolutions'}
        </div>
      ),
      hoverState: {
        title: (
          <div className={css.stepsSection2Card3Hover}>
            {'Avoid paying for unnecessary maintenance\u00a0visits.'}
          </div>
        ),
        body: 'We guide Residents to solve issues themselves before sending a Pro, saving them time and you\u00a0money.',
      },
    },
  ],
  section3: [
    {
      src: 'homeowner-lp/ho_lp_section_09.png',
      title: (
        <div className={css.stepsSection3Card1}>
          {'Get qualified & hand\u00a0us\u00a0the\u00a0keys.'}
        </div>
      ),
    },
    {
      src: 'homeowner-lp/ho_lp_section_11.png',
      title: (
        <div className={css.stepsSection3Card2}>
          We’ll find someone to{' '}
          <span className="text-dark-gray line-through">rent</span>{' '}
          {'love\u00a0your\u00a0home.'}
        </div>
      ),
    },
    {
      src: 'homeowner-lp/ho_lp_section_10.png',
      title: 'Get Guaranteed Rent and\u00a0never\u00a0worry\u00a0again.',
    },
  ],
} satisfies Record<string, StepsSectionData[]>;

export const LandingPage: NextPage<ServerProps> & StaticMethods = ({
  blogPosts,
  homeownersCount,
  coverageCities,
}) => {
  const [isStickyBarVisible, setIsStickyBarVisible] = useState(false);
  const priceTableRef = useRef<HTMLDivElement>(null);
  const region = useStore(regionSelector);

  const ctaRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(ctaRef);
  const previousIsOnScreen = useRef(isOnScreen);

  const router = useRouter();
  const { user } = useUser();

  const cookies = parseCookies();

  const comeFromMobileApp = Boolean(
    cookies[COOKIES_CONFIG.MOBILE_APP.name] === 'true',
  );
  const headerImageName =
    REGION_ID_TO_COVER_IMAGE_MAP[region.id] || DEFAULT_COVER_IMAGE;

  const AnimatedStickyNavbar = animated(StickyNavbar);
  const AnimatedFootbar = animated(Footbar);
  const transitions = useTransition(isStickyBarVisible, {
    from: { y: -100 },
    enter: { y: 0 },
    leave: { y: -100 },
  });

  useEffect(() => {
    if (isOnScreen && !previousIsOnScreen.current) {
      setIsStickyBarVisible(false);
    } else if (!isOnScreen && previousIsOnScreen.current) {
      setIsStickyBarVisible(true);
    }

    previousIsOnScreen.current = isOnScreen;
  }, [isOnScreen]);

  function scrollToPriceTable() {
    const priceTable = priceTableRef.current;
    if (priceTable) {
      window.scrollBy({
        top: priceTable.getBoundingClientRect().top,
        behavior: 'smooth',
      });
    }
  }

  let metaTitle = STRINGS['meta.title'];
  let metaDescription = STRINGS['meta.description'];

  if (region.id) {
    metaTitle = template(STRINGS['meta.title.withRegion'], {
      regionName: region.region,
    });
    metaDescription = template(STRINGS['meta.description.withRegion'], {
      regionName: region.region,
    });
  }

  const isFloridaRegion = region.state === US_STATES_CODES.FL;
  const isSanDiegoRegion = region?.name === 'SanDiego';
  const isBayArea = region?.name === 'BayArea';

  let coverSubtitle = STRINGS['cover.subtitle'];
  if (region?.name === 'Nationally') {
    coverSubtitle = STRINGS['cover.subtitle-no-region'];
  } else {
    coverSubtitle = template(STRINGS['cover.subtitle'], {
      region: region?.region,
    });
  }

  const { leftPlane, rightPlane } = useSpring({
    loop: true,
    config: { duration: 9000 },
    from: { leftPlane: '-50%', rightPlane: '100%' },
    to: [{ leftPlane: '100%' }, { rightPlane: '-100%  ' }],
  });

  const handleRentEstimateClick = async () => {
    await trackEventService({
      eventName: GA_EVENTS.FLOW_STARTED,
      metadata: {
        ...(user?.userId && { userId: user.userId }),
        variant: GA_ENTRY_POINTS.HO_STICKY_NAV_BAR,
        experiment: GA_EXPERIMENT.RENT_ESTIMATE,
      },
    });

    router.push(PATHS.RENT_ESTIMATE_FLOW);
  };

  return (
    <PageViewTracker
      name="homeowners_landing_page"
      trackingData={{
        [NEW_GUARANTEED_TIME_TO_LOVE_HOMEOWNERS_LANDING_PAGE_EXPERIMENT.name]:
          getExperimentVariantFromCookies(
            NEW_GUARANTEED_TIME_TO_LOVE_HOMEOWNERS_LANDING_PAGE_EXPERIMENT.name,
          ),
      }}
    >
      <IntercomWidget
        isEnabled={INTERCOM_CONFIG.homeowner.isEnabled}
        isStickyBarVisible={isStickyBarVisible}
      />

      <MetaSEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
        }}
      />

      <div className={clsx('w-full')}>
        <div className="bg-white">
          <div className="h-xs container relative flex justify-end">
            <Text
              as="h1"
              variant="p2"
              className="bottom-xs text-dark-gray absolute z-10"
            >
              {!region?.name || region?.name === 'Nationally'
                ? 'Property Management With Guaranteed Rent'
                : `${region?.region} Property Management`}
            </Text>
          </div>
        </div>
        <Cover
          imageSrc={`/homeowner-lp/headers/${headerImageName}`}
          subtitle={coverSubtitle}
          ctaRef={ctaRef}
        />

        <div className="mb-sm relative">
          <div className="relative w-full overflow-hidden">
            <animated.div
              style={{
                position: 'relative',
                top: 0,
                left: 0,
                bottom: 0,
                width: '100%',
                display: 'block',
                transform: leftPlane.interpolate((val) => `translateX(${val})`),
              }}
            >
              <div className="z-0">
                <Image
                  src="/belong-x/lp_pro_plane_left_ho.png"
                  alt=""
                  className={css.plane}
                />
              </div>
            </animated.div>
          </div>
          <div className="relative w-full overflow-hidden">
            <animated.div
              style={{
                position: 'relative',
                top: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                display: 'block',
                transform: rightPlane.interpolate(
                  (val) => `translateX(${val})`,
                ),
              }}
            >
              <div className="z-0">
                <Image
                  src="/belong-x/lp_pro_plane_rigjt_ho.png"
                  alt=""
                  className={clsx(css.plane)}
                />
              </div>
            </animated.div>
          </div>
        </div>

        <div className="relative">
          {isFloridaRegion && (
            <article className="mb-5xl mt-3xl px-2sm lg:mb-6xl container">
              <RefreshMiamiBanner />
            </article>
          )}
          {isBayArea && (
            <article className="mb-5xl mt-3xl px-2sm lg:mb-6xl container">
              <SfistBayAreaBanner />
            </article>
          )}
          {isSanDiegoRegion && (
            <article className="mb-5xl mt-3xl px-2sm lg:mb-6xl">
              <SanDiegoShortTermRentalsBanner />
            </article>
          )}
          <div
            className="mb-lg mt-3xl md:mb-2xl container flex flex-col items-center"
            id="presentation-video"
          >
            <Text
              variant="h2"
              fontWeight="semibold"
              className={clsx(
                'mb-xs text-center md:mb-0',
                css.videoTitleContainer,
              )}
            >
              {STRINGS['videoSection.title']}
            </Text>
            <Text
              variant="h3"
              className={clsx('text-center', css.videoTitleContainer)}
            >
              {STRINGS['videoSection.subtitle']}
            </Text>
          </div>
          <div className="mb-5xl md:mb-6xl flex flex-1 justify-center">
            <VideoSideContents autoplay={!comeFromMobileApp} />
          </div>
          <div className="mb-5xl md:mb-6xl w-full">
            <TownTourSection
              intro={
                <>
                  <Text
                    variant="h2"
                    fontWeight="semibold"
                    className={clsx(
                      css.stepsSection2Title,
                      'mb-xs lg:max-w-none',
                    )}
                  >
                    {STRINGS['how-it-works.title']}
                  </Text>
                  <Text variant="body">{STRINGS['how-it-works.subtitle']}</Text>
                </>
              }
            />
          </div>
          <HomeownersTrustBelongSection homeownersCount={homeownersCount} />
          <ImpressionTracker section="testimonial_carousel">
            <div className="mb-xl mt-2xl md:mb-2xl container flex flex-col items-center">
              <Text
                variant="h2"
                fontWeight="semibold"
                className={clsx(
                  'mb-sm text-center',
                  css.testimonialTitleContainer,
                )}
              >
                {STRINGS['testimonialsSection.title']}
              </Text>
              <GoogleStars />
            </div>

            <div className="carousel-container mb-5xl md:mb-6xl flex justify-center">
              <TestimonialsCarousel />
            </div>
          </ImpressionTracker>
          <div className="mb-2xl flex w-full flex-col items-center overflow-hidden md:container lg:w-auto">
            <Text
              variant="h2"
              fontWeight="semibold"
              className={clsx(css.stepsSection1Title, 'mb-2xl text-center')}
            >
              {STRINGS['stepsSection1.title']}
            </Text>
            <StepsSection data={STEPS_SECTIONS_DATA.section1} />
          </div>
          <div className="mb-5xl md:mb-6xl container">
            <CalculatorSectionVariant open />
          </div>
          <div className="mb-5xl md:mb-6xl w-full">
            <LessVacancySection />
          </div>
          <div className="carousel-container mb-5xl md:mb-6xl flex flex-col items-center">
            <Text
              variant="h2"
              fontWeight="semibold"
              className={clsx(
                css.stepsSection2Title,
                'mb-xs text-center md:max-w-none',
              )}
            >
              {STRINGS['stepsSection2.title']}
            </Text>
            <Text variant="h3" className="mb-2xl text-center">
              {STRINGS['stepsSection2.subtitle']}
            </Text>

            <StepsSection data={STEPS_SECTIONS_DATA.section2} />
          </div>
          <div id="pricing-table" className="mb-5xl lg:mb-6xl container">
            <PriceTablePreview
              title={STRINGS['priceTable.title']}
              subtitle={STRINGS['priceTable.subtitle']}
              regionId={region.id}
              showMore={STRINGS['priceTable.showMore']}
              showLess={STRINGS['priceTable.showLess']}
              priceTableRef={priceTableRef}
            />
          </div>

          <div className="carousel-container mb-5xl md:mb-6xl flex flex-col items-center">
            <Text
              variant="h2"
              fontWeight="semibold"
              className={clsx(
                css.stepsSection3Title,
                'mb-2xl text-center md:max-w-none',
              )}
            >
              {STRINGS['stepsSection3.title']}
            </Text>
            <StepsSection data={STEPS_SECTIONS_DATA.section3} showLines />
          </div>
          {blogPosts?.length > 0 && (
            <div className="carousel-container mb-5xl md:mb-6xl flex flex-col items-center">
              <Text
                variant="h2"
                fontWeight="semibold"
                className="mb-lg md:mb-xl"
              >
                {STRINGS['blogSection.title']}
              </Text>
              <div className="mb-lg md:mb-xl w-full">
                <BlogCarousel blogPosts={blogPosts} />
              </div>
              <Link
                href="/blog"
                variant="outline"
                buttonType="secondary"
                size="large"
              >
                {STRINGS['blogSection.ctaLabel']}
              </Link>
            </div>
          )}
          <div className="mb-3xl md:mb-4xl container flex flex-col  items-center justify-center">
            <div className="mb-2xl">
              <Text variant="h2" fontWeight="semibold">
                {STRINGS['map.title']}
              </Text>
            </div>
            <CoverageMap coverageCities={coverageCities} />
          </div>
          {transitions(
            (styles, isStickyBarVisible) =>
              isStickyBarVisible && (
                <>
                  <AnimatedStickyNavbar
                    style={styles}
                    handlePricingClick={scrollToPriceTable}
                  />
                  <AnimatedFootbar
                    style={{ y: styles.y.to((y) => -y) }}
                    buttonLeft={
                      <>
                        <div className="contents md:hidden">
                          {NAVBAR_ACTIONS.REGION_PHONE(region)}
                        </div>
                        <div className="hidden md:contents">
                          <Button
                            variant="text"
                            buttonType="secondary"
                            size="auto"
                            onClick={handleRentEstimateClick}
                          >
                            <span className="text-p1 leading-p1">{`Get\u00a0Rent\u00a0Estimate`}</span>
                          </Button>
                        </div>
                      </>
                    }
                    buttonRight={NAVBAR_ACTIONS.SEE_QUALIFY()}
                  />
                </>
              ),
          )}
        </div>
      </div>
      <ReferralMessage />
    </PageViewTracker>
  );
};

type StaticMethods = {
  getLayout?: (page: ReactNode, pageProps: ServerProps) => ReactNode;
};

export type ServerProps = {
  blogPosts: PrismicDocument[];
  homeownersCount: number;
  initialZustandState?: InitialZustandState;
  coverageCities: CoverageCity[];
  heroCopyVariant?: string;
};

export function getLandingPageGetStaticProps(regionSlug?: string) {
  // Named `getStaticProps` but wired up as `getServerSideProps` on both pages
  // that use it, which is the context `getRegionFromCookies` below wants.
  return async (ctx: GetServerSidePropsContext) => {
    // const props: ServerProps = { blogPosts: [], homeownersCount: 1000, coverageCities: [], variant: experiment };
    const props: ServerProps = {
      blogPosts: [],
      homeownersCount: 1000,
      coverageCities: [],
    };
    const client = createClient();
    const apiClient = buildServerClient();
    const regions = await fetchRegionsService(apiClient);

    try {
      const citiesByRegion = await fetchCityListService();

      props.coverageCities = citiesByRegion
        .map((city) => ({
          href: `${BASE_PATHS.HOMEOWNER}/${kebabCase(
            city.city ?? '',
          )}-${city.stateId.toLowerCase()}-property-management`,
          displayName: city.city ?? '',
          regionSlug: kebabCase(city.region.name ?? ''),
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    } catch (e) {
      console.error(e);
    }

    // TODO: We need to get rid of this if we ever want this LP to go back to being fully static
    const regionFromCookies = getRegionFromCookies(ctx, somewhereElse);
    const region = regions.find(
      (region) => region.nameKebabCase === regionSlug,
    );
    props.initialZustandState = { region: regionFromCookies };
    if (region) {
      props.initialZustandState = { region };
    }

    try {
      const response = await fetchBlogPostsList({
        client,
        pageProps: { pageSize: 6 },
      });
      const blogProfiles = await fetchAllBlogProfiles(client);
      props.blogPosts = response.results
        .filter(
          (result) => typeof result.data?.cover_image?.url !== 'undefined',
        )
        .map((result) => {
          const { body: _body, author, ...restData } = result.data;
          const _author = blogProfiles.find(
            (profile) => profile.uid === author,
          );

          return { ...result, author: _author, data: restData };
        });
    } catch {
      // intentional
    }

    try {
      props.homeownersCount = await fetchHomeownerCountService(apiClient);
    } catch {
      // intentional
    }

    // Priority order for heroCopyVariant:
    // 1. experiments cookie — returning visitor, stable assignment.
    // 2. getVariant fallback — first visit / edge cases where middleware didn't run.
    const experimentCookies = getExperimentsFromCookies(ctx);
    const cookies = parseCookies(ctx);
    const trackingId =
      cookies[COOKIES_CONFIG.TRACKING_ID.name] ||
      cookies[COOKIES_CONFIG.LEGACY_TRACKING_ID.name] ||
      nanoid();

    props.heroCopyVariant =
      experimentCookies[HOMEOWNERS_HERO_COPY_V2_EXPERIMENT.name] ??
      getVariant(trackingId, HOMEOWNERS_HERO_COPY_V2_EXPERIMENT);

    return { props };
  };
}
